import React, { useEffect, useMemo } from "react";
import _ from 'i18n';
import EventLineChartLegendRow from "./EventLineChartLegendRow";
import { Col, Row } from "antd";
import { formatAmount } from "../../../../services/utils";
import { connect } from "react-redux";
import { setEventStatsSaison } from "../../../../actions/actions";
import { getSeasonsValues } from "../../../../actions/thunks/eventStats";

const calculateRegistrantsNumberByAppType = (lineName, transformedData, startMonth, endMonth) => {
  if (!transformedData || transformedData.length === 0) {
    return 0;
  }

  return transformedData.reduce((tot, record, index) => {
    if (!startMonth && !endMonth) {
      return tot + record[lineName];
    }

    return startMonth <= index + 1 && index + 1 <= endMonth ? tot + record[lineName] : tot;
  }, 0);
};

const calculateRegistrantsNumberByAppTypeFormat = (lineName, transformedSourceData, startMonth, endMonth) => {
  if (!transformedSourceData || transformedSourceData.length === 0) {
    return 0;
  }

  return transformedSourceData.reduce((tot, record, index) => {
    if (!startMonth && !endMonth) {
      return tot + record[lineName];
    }

    return startMonth <= index + 1 && index + 1 <= endMonth ? tot + record[lineName] : tot;
  }, 0);
};

const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';

  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }

  return color;
};

const getDaysOfMonthFromString = (monthYearArray) => {
  const daysArray = [];

  if (!Array.isArray(monthYearArray)) {
    return daysArray;
  }

  monthYearArray.forEach(monthYear => {
    const [year, month] = monthYear.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();

    for (let i = 1; i <= daysInMonth; i++) {
      const day = String(i).padStart(2, '0');
      const monthFormatted = String(month).padStart(2, '0');
      daysArray.push(`${day}/${monthFormatted}`);
    }
  });

  return daysArray;
};

const getYearsArray = () => {
  const currentYear = new Date().getFullYear();
  const yearsArray = [];

  for (let year = 2018; year <= currentYear; year++) {
    yearsArray.push(year);
  }

  return yearsArray;
};

const EventLineChartLegendFormat = (props) => {
  const {
    auth,
    eventStatsFilter,
    seasons,
    getSeasonsValues,
    updateChartLinesToShow,
    fetching,
    isError,
    formatData,
    formatCAData,
    chartLinesToShowFormat,
    sourceFormatData,
    sourceFormatDataCA,
    year,
    sourceYear,
    type,
    startMonth,
    endMonth,
    seasonsToShow,
    frequency,
    period,
    showSpinner,
  } = props;

  const getSeasons = () => {
    const params = {};

    if (auth.currentClient) {
      params.issuerId = auth.currentClient.id;
    }

    getSeasonsValues(params);
  };

  useEffect(() => {
    getSeasons();
  }, [eventStatsFilter.year]);

  const months = useMemo(() => ["Jan", "Fév", "Mar", "Avr", "Mai", "Jui", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"], []);
  const daysOfMonth = useMemo(() => getDaysOfMonthFromString(period), [period]);
  const years = useMemo(() => getYearsArray(), []);

  const monthOrDaysOrYears = useMemo(() => {
    switch (frequency) {
      case 'MONTHLY':
        return months;
      case 'DAILY':
        return daysOfMonth;
      case 'YEARLY':
        return years;
      default:
        return [];
    }
  }, [frequency, months, daysOfMonth, years]);

  const safeFormatData = formatData || {};
  const safeFormatCAData = formatCAData || {};
  const safeSourceFormatData = sourceFormatData || {};
  const safeSourceFormatDataCA = sourceFormatDataCA || {};

  const transformedData = useMemo(() => {
    const result = [];

    monthOrDaysOrYears.forEach((monthOrDayOrYear, index) => {
      const monthData = { name: monthOrDayOrYear };

      Object.keys(safeFormatData).forEach(entity => {
        if (entity.length === 0) return;

        let entityTotal = 0;
        if (safeFormatData[entity][index]) {
          entityTotal =
            safeFormatData[entity][index].formations +
            safeFormatData[entity][index].cycles +
            safeFormatData[entity][index].cycles_essential +
            safeFormatData[entity][index].saisons +
            safeFormatData[entity][index].monthly_premium +
            safeFormatData[entity][index].yearly_premium +
            safeFormatData[entity][index].congrès;
        }

        monthData[entity] = entityTotal;
      });

      result.push(monthData);
    });

    return result;
  }, [safeFormatData, monthOrDaysOrYears]);

  const transformedSourceData = useMemo(() => {
    const result = [];

    if (!sourceFormatData || !sourceFormatDataCA) {
      return result;
    }

    monthOrDaysOrYears.forEach((monthOrDayOrYear, index) => {
      const monthData = { name: monthOrDayOrYear };

      Object.keys(safeSourceFormatData).forEach(entity => {
        if (entity.length === 0) return;

        let entityTotal = 0;
        if (safeSourceFormatData[entity][index]) {
          entityTotal =
            safeSourceFormatData[entity][index].formations +
            safeSourceFormatData[entity][index].cycles +
            safeSourceFormatData[entity][index].cycles_essential +
            safeSourceFormatData[entity][index].saisons +
            safeSourceFormatData[entity][index].monthly_premium +
            safeSourceFormatData[entity][index].yearly_premium +
            safeSourceFormatData[entity][index].congrès;
        }

        monthData[entity] = entityTotal;
      });

      result.push(monthData);
    });

    return result;
  }, [safeSourceFormatData, sourceFormatDataCA, monthOrDaysOrYears]);

  const transformedSourceCA = useMemo(() => {
    const result = [];

    if (!sourceFormatDataCA) {
      return result;
    }

    monthOrDaysOrYears.forEach((monthOrDayOrYear, index) => {
      const monthData = { name: monthOrDayOrYear };

      Object.keys(safeSourceFormatDataCA).forEach(entity => {
        if (entity.length === 0) return;

        let entityTotal = 0;
        if (safeSourceFormatDataCA[entity][index]) {
          entityTotal =
            safeSourceFormatDataCA[entity][index].formations +
            safeSourceFormatDataCA[entity][index].cycles +
            safeSourceFormatDataCA[entity][index].cycles_essential +
            safeSourceFormatDataCA[entity][index].saisons +
            safeSourceFormatDataCA[entity][index].monthly_premium +
            safeSourceFormatDataCA[entity][index].yearly_premium +
            safeSourceFormatDataCA[entity][index].congrès;
        }

        monthData[entity] = entityTotal;
      });

      result.push(monthData);
    });

    return result;
  }, [safeSourceFormatDataCA, monthOrDaysOrYears]);

  const transformedDataCA = useMemo(() => {
    const result = [];

    monthOrDaysOrYears.forEach((monthOrDayOrYear, index) => {
      const monthData = { name: monthOrDayOrYear };

      Object.keys(safeFormatCAData).forEach(entity => {
        if (entity.length === 0) return;

        let entityTotal = 0;
        if (safeFormatCAData[entity][index]) {
          entityTotal =
            safeFormatCAData[entity][index].formations +
            safeFormatCAData[entity][index].cycles +
            safeFormatCAData[entity][index].cycles_essential +
            safeFormatCAData[entity][index].saisons +
            safeFormatCAData[entity][index].monthly_premium +
            safeFormatCAData[entity][index].yearly_premium +
            safeFormatCAData[entity][index].congrès;
        }

        monthData[entity] = entityTotal;
      });

      result.push(monthData);
    });

    return result;
  }, [safeFormatCAData, monthOrDaysOrYears]);

  let eventTypes = [];

  if (auth.currentClient.businessType === 'SUPER_CHAIN') {
    eventTypes = [
      { legend: _('TRAININGS'), color: "#18A0FB", lineName: "formations" },
      { legend: _('CYCLES'), color: "#5F5DE8", lineName: "cycles" },
      { legend: _('CYCLES_ESSENTIAL'), color: "#14B7AD", lineName: "cycles_essential" },
      { legend: _('SEASONS'), color: "#F47F4D", lineName: "saisons" },
      { legend: _('MONTHLY_PREMIUM'), color: "#FB1E5B", lineName: "monthly_premium" },
      { legend: _('YEARLY_PREMIUM'), color: "#FFD433", lineName: "yearly_premium" },
      { legend: _('CONGRESS'), color: "#6D7F92", lineName: "congrès" },
    ];
  } else if (auth.currentClient.businessType === 'MASTER_CHAIN') {
    eventTypes = [
      { legend: _('TRAININGS'), color: "#18A0FB", lineName: "formations" },
      { legend: _('CYCLES'), color: "#5F5DE8", lineName: "cycles" },
    ];
  } else {
    eventTypes = [
      { legend: _('TRAININGS'), color: "#18A0FB", lineName: "formations" },
    ];
  }

  console.log("==== transformedData", transformedData, transformedDataCA);
  console.log("==== transformedSourceData", transformedSourceData, transformedSourceCA);

  let totalRegistrantNumber = 0;
  let totalCANumber = 0;
  let totalSourceRegistrantNumber = 0;
  let totalSourceCANumber = 0;

  if (!showSpinner) {
    seasonsToShow.forEach(({ lineName }) => {
      if (lineName.length === 0) return;
      if (chartLinesToShowFormat.includes(lineName)) {
        totalRegistrantNumber += calculateRegistrantsNumberByAppType(lineName, transformedData, startMonth, endMonth);
        totalCANumber += calculateRegistrantsNumberByAppType(lineName, transformedDataCA, startMonth, endMonth);

        if (sourceFormatData) {
          totalSourceRegistrantNumber += calculateRegistrantsNumberByAppTypeFormat(lineName, transformedSourceData, startMonth, endMonth);
          totalSourceCANumber += calculateRegistrantsNumberByAppTypeFormat(lineName, transformedSourceCA, startMonth, endMonth);
        }
      }
    });
  }

  return (
    <div>
      <div style={{ height: '420px', overflowY: 'auto' }}>
        {sourceFormatData && (
          <Row className={'legend-row'}>
            <Col span={7} />
            <Col span={8} className={'registrants-number flex justify-center'} style={{ justifyContent: "end", color: "#29394d", opacity: 0.7 }}>{year}</Col>
            <Col span={8} className={'registrants-number flex justify-center'} style={{ justifyContent: "end", color: "#29394d" }}>{sourceYear}</Col>
          </Row>
        )}
        <Row className={'legend-row'}>
          <Col span={sourceFormatData ? 7 : 15} />
          <Col span={4} className={'registrants-number flex'} style={{ paddingLeft: '20px', color: "#29394d", opacity: sourceFormatData ? 0.7 : 1 }}>Inscriptions</Col>
          <Col span={4} className={'registrants-number flex justify-center'} style={{ justifyContent: "right", color: "#29394d", opacity: sourceFormatData ? 0.7 : 1 }}>C.A</Col>
          {sourceFormatData && <Col span={4} className={'registrants-number flex'} style={{ paddingLeft: '20px', color: "#29394d" }}>Inscriptions</Col>}
          {sourceFormatData && <Col span={4} className={'registrants-number flex justify-center'} style={{ justifyContent: "right", color: "#29394d" }}>C.A</Col>}
        </Row>
        {seasonsToShow.map(({ legend, color, lineName }, index) => lineName.length > 0 && (
          <React.Fragment key={lineName}>
            <hr />
            <EventLineChartLegendRow
              legend={legend}
              color={color}
              registrantNumber={calculateRegistrantsNumberByAppType(lineName, transformedData, startMonth, endMonth)}
              caNumber={calculateRegistrantsNumberByAppType(lineName, transformedDataCA, startMonth, endMonth)}
              updateChartLinesToShow={updateChartLinesToShow}
              chartLinesToShow={chartLinesToShowFormat}
              fetching={showSpinner}
              isError={isError}
              lineName={lineName}
              sourceRegistrantNumber={fetching === false && sourceFormatData ? calculateRegistrantsNumberByAppTypeFormat(lineName, transformedSourceData, startMonth, endMonth) : null}
              sourceCARegistrantNumber={fetching === false && sourceFormatData ? calculateRegistrantsNumberByAppTypeFormat(lineName, transformedSourceCA, startMonth, endMonth) : null}
              type={type}
              startMonth={startMonth}
              endMonth={endMonth}
              sourceData={transformedSourceData}
            />
            {index === seasonsToShow.length - 1 && <hr />}
          </React.Fragment>
        ))}
      </div>
      {showSpinner === false && (
        <Row className={'legend-row'}>
          <Col span={sourceFormatData ? 5 : 15} className={'registrants-number flex '} style={{ color: "#29394d" }}>Total</Col>
          {sourceFormatData && (
            <Col span={2} className={'registrants-number flex'} style={{ marginLeft: "6px" }}>
              {Math.round((((totalSourceCANumber / 100) - (totalCANumber / 100)) / (totalCANumber / 100 == 0 ? 1 : totalCANumber / 100)) * 100) > 0 ? (
                <React.Fragment>
                  <img src="/img/icons/manage-BM/BM_plus.svg" />
                  <span className={'ml-1'} style={{ color: '#02af8e', fontSize: '12px' }}>{Math.abs(parseFloat(((((totalSourceCANumber / 100) - (totalCANumber / 100)) / (totalCANumber / 100 == 0 ? totalSourceCANumber / 100 : totalCANumber / 100)) * 100).toFixed(2)))}% </span>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <img src="/img/icons/manage-BM/BM_moins.svg" className={'mr-1'} />
                  <span className={'mr-1'} style={{ color: '#fe3745', fontSize: '12px' }}>{Math.abs(parseFloat(((((totalSourceCANumber / 100) - (totalCANumber / 100)) / (totalCANumber / 100 == 0 ? totalSourceCANumber / 100 : totalCANumber / 100)) * 100).toFixed(2)))}% </span>
                </React.Fragment>
              )}
            </Col>
          )}
          <Col span={4} className={'registrants-number flex'} style={{ paddingLeft: '20px', color: "#29394d", opacity: sourceFormatData ? 0.7 : 1 }}>{totalRegistrantNumber}</Col>
          <Col span={4} className={'registrants-number flex '} style={{ justifyContent: "right", color: "#29394d", opacity: sourceFormatData ? 0.7 : 1 }}>{formatAmount(totalCANumber)}</Col>
          {sourceFormatData && <Col span={4} className={'registrants-number flex'} style={{ paddingLeft: '20px', color: "#29394d" }}>{totalSourceRegistrantNumber}</Col>}
          {sourceFormatData && <Col span={4} className={'registrants-number flex'} style={{ justifyContent: "right", color: "#29394d" }}>{formatAmount(totalSourceCANumber)}</Col>}
          <Col span={1} />
        </Row>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  eventStatsFilter: state.filters.eventStatsFilter,
  seasons: state.seasons,
  auth: state.auth,
});

const mapDispatchToProps = {
  setEventStatsSaison,
  getSeasonsValues,
};

export default connect(mapStateToProps, mapDispatchToProps)(EventLineChartLegendFormat);



// WEBPACK FOOTER //
// ./app/components/Admin/EventStats/EventRegistrantLinechart/EventLineChartLegendFormat.js